import { AddEditItemQuantityProps } from '../../models/table-models';
import { ItemQuantity, PassportId, PassportType } from '../../models/warehouse-models';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import React, { useEffect, useState } from 'react';
import { resourceCache, useAsyncResource } from 'use-async-resource';
import {
    getQuantityCount,
    UseAddQuantity,
    useGetAllPassportTypes,
    useGetAllShortDevicePassportsWithoutSaleFilter,
    UseUpdateQuantity,
} from '../../utils/warehouses-hooks';
import { Form } from 'react-bootstrap';
import CommonInputText from '../../components/CommonInputText';
import Select, { SingleValue } from 'react-select';
import { stringsToOptions, stringToOption } from '../../components/FormMultiSelect';
import { optionSelectStyles } from '../../styles/selectStyles';
import {toast} from "react-toastify";
import {CustomToastContent} from "../../components/CustomToastContent";
import {toastOptionsTopRight} from "../../utils/Enums/ToastOptions";
import { getGeneratedBatchCode } from '../../utils/batch-code';

const getBatchCode = () => getGeneratedBatchCode('L');

export const AddEditQuantity = ({
    itemToEdit,
    setShowModal,
    itemRelatedId,
    refetchEntitiesHook,
    warehouseId,
    passport,
}: AddEditItemQuantityProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'table' });
    const commonTranslation  = useTranslation('translation', { keyPrefix: 'common' });
    const defaultBatchCode = React.useMemo(
        () => warehouseId ? getBatchCode() : itemToEdit?.batchCode ?? getBatchCode(),
        [itemToEdit?.batchCode, warehouseId]
    );

    const { register, handleSubmit, formState, control, resetField, watch, setValue } = useForm<ItemQuantity>({
        mode: 'all',
        defaultValues: {
            passport: passport,
            hasContainer: itemToEdit?.hasContainer ?? false,
            batchCode: defaultBatchCode,
        }
    });
    const [oilInputMode, setOilInputMode] = useState<'count' | 'millilitres'>(
        itemToEdit?.availableAmount != null ? 'millilitres' : 'count'
    );
    const [getAllPassportTypes] = useAsyncResource(useGetAllPassportTypes, []);
    const [readShortDevicePassports] = useAsyncResource(useGetAllShortDevicePassportsWithoutSaleFilter, []);
    const shortDevicePassports = readShortDevicePassports();

    // Resolves a passport's item type (OIL/DEVICE/...) from its id; type lives on the PassportType wrapper, not on PassportId.
    const passportTypeOf = (passportId?: number) =>
        getAllPassportTypes().find((item) => item.passports.some((p) => p.passportId === passportId))?.type;

    const useSubmitHandlerAdd = async (formValue: ItemQuantity) => {
        const { name, passportId, passport } = passports.find(
            (value) => value.passport === formValue.passport.passport
        ) ?? {
            name: '',
            passportId: -1,
            passport: '',
        };

        const type = passportTypeOf(passportId);
        const batchCode = formValue.batchCode || getBatchCode();

        await UseAddQuantity(
            {
                ...formValue,
                count: isOil && oilInputMode === 'millilitres' ? 1 : Number(formValue.count),
                availableAmount: isOil && oilInputMode === 'count'
                    ? undefined
                    : formValue.availableAmount,
                batchCode,
                item:type,
                passport: {
                    name,
                    passportId,
                    passport,
                },
            },
            String(warehouseId)
        );
        resourceCache(getQuantityCount).clear();
        refetchEntitiesHook();
        setShowModal(false);
        toast.success(<CustomToastContent content={[t('quantitySuccess')]} />, toastOptionsTopRight);
    };

    const useSubmitHandlerEdit = async (formValue: ItemQuantity) => {
        const { name,passportId, passport } = passports.find(
            (value) => value.passport === formValue.passport.passport
        ) ?? {
            name:'',
            passportId: -1,
            passport: '',
        };

        const type = passportTypeOf(passportId);
        const batchCode = formValue.batchCode || getBatchCode();
        if (warehouseId) {
            await UseAddQuantity(
                {
                    ...formValue,
                    count: isOil && oilInputMode === 'millilitres' ? 1 : Number(formValue.count),
                    availableAmount: isOil && oilInputMode === 'count'
                        ? undefined
                        : formValue.availableAmount,
                    batchCode,
                    item:type,
                    passport: {
                        name,
                        passportId,
                        passport,
                    },
                },
                String(warehouseId)
            )
            resourceCache(getQuantityCount).clear();

            refetchEntitiesHook();
            setShowModal(false);
            toast.success(<CustomToastContent content={[t('quantitySuccess')]} />, toastOptionsTopRight);
        } else {
            await UseUpdateQuantity(
                {
                    ...formValue,
                    batchCode,
                    item:type,
                    passport: {
                        name,
                        passportId,
                        passport,
                    },
                },
                itemToEdit?.id
            )

            refetchEntitiesHook();
            setShowModal(false);
            toast.success(<CustomToastContent content={[t('quantityUpdated')]} />, toastOptionsTopRight);
        }
    };

    const updatePassport = (
        newValue: SingleValue<PassportType>,
        onFieldChange: (arg0?: string) => void
    ) => {
        onFieldChange(newValue?.type);
        newValue?.passports && setPassports(newValue?.passports);
        resetField('passport.passport', { defaultValue: '' });
        setValue('hasContainer', false);
    };

    const [passports, setPassports] = useState<PassportId[]>(getAllPassportTypes().flatMap(item => item.passports));

    // Oil stock tracks essence (ml) independently of bottle count; show the ml field only for oil passports.
    const selectedPassportName = watch('passport.passport') ?? itemToEdit?.passport?.passport ?? passport?.passport;
    const selectedPassportId = passports.find((p) => p.passport === selectedPassportName)?.passportId;
    const isOil = (passportTypeOf(selectedPassportId) ?? itemToEdit?.item) === 'OIL';
    const selectedDevicePassport = shortDevicePassports.find((devicePassport) => devicePassport.id === selectedPassportId);
    const selectedSupportedContainerType = selectedDevicePassport?.supportedContainerType ?? null;
    const isDevice = (passportTypeOf(selectedPassportId) ?? itemToEdit?.item) === 'DEVICE';
    const supportsBottle = isDevice && selectedSupportedContainerType === 'BOTTLE';
    useEffect(() => {
        if (!itemToEdit && supportsBottle && selectedDevicePassport?.emptyContainerPassportId != null) {
            setValue('hasContainer', true);
        }
    }, [itemToEdit, selectedDevicePassport?.emptyContainerPassportId, setValue, supportsBottle]);

    const updateSelectedPassport = (passportName?: string) => {
        const nextPassportId = passports.find((p) => p.passport === passportName)?.passportId;
        const nextDevicePassport = shortDevicePassports.find((devicePassport) => devicePassport.id === nextPassportId);
        setValue('hasContainer',
            nextDevicePassport?.supportedContainerType === 'BOTTLE' &&
            nextDevicePassport?.emptyContainerPassportId != null
        );
    };

    return (
        <Form
            id="form"
            className="d-flex flex-column align-items-center p-4 w-100"
            noValidate
            onSubmit={
                itemToEdit ? handleSubmit(useSubmitHandlerEdit) : handleSubmit(useSubmitHandlerAdd)
            }
        >
            <div className="d-flex w-100 justify-content-around align-items-center">
                <Form.Group
                    className=" d-flex flex-column mb-2 w-50 align-items-start ms-3"
                    controlId="passport.passport"
                >
                    <Form.Label>{t('passport')}</Form.Label>
                    <Controller
                        control={control}
                        name="passport.passport"
                        defaultValue={itemToEdit?.passport.passport}
                        rules={{ required: true }}
                        render={({ field }) => (
                            <>
                                <Select
                                    className="w-100"
                                    value={stringToOption(field.value)}
                                    onChange={(newValue) => {
                                        field.onChange(newValue?.value);
                                        updateSelectedPassport(newValue?.value);
                                    }}
                                    options={stringsToOptions(passports.map((value) => value.passport))}
                                    getOptionLabel={({ name }) => {
                                        const f = passports.find(passport => passport.passport === name);
                                        return f?.name ?? ""
                                    }}
                                    getOptionValue={({ value }) => value}
                                    styles={optionSelectStyles}
                                    isDisabled={passport !== undefined || itemToEdit !== undefined}
                                />
                                {formState.errors.passport && (
                                    <p className={`input-error`}>{'*'+t('required')}</p>
                                )}
                            </>
                        )}
                    />
                </Form.Group>
            </div>

            {isDevice && selectedSupportedContainerType && (
                <Form.Group className="d-flex flex-row gap-1 mt-2 mb-2 align-items-center w-100" controlId="supportedContainerType">
                    <Form.Label className="mb-0">{t('supportedContainerType')}:</Form.Label>
                    <div>{commonTranslation.t(selectedSupportedContainerType)}</div>
                </Form.Group>
            )}

            {supportsBottle && (
                <Form.Group className="d-flex align-items-center mb-2 w-100" controlId="hasContainer">
                    <Controller
                        control={control}
                        name="hasContainer"
                        render={({ field }) => (
                            <Form.Check className="d-flex align-items-center gap-2 mb-0">
                                <Form.Check.Input
                                    className="mt-0"
                                    type="checkbox"
                                    checked={!!field.value}
                                    onChange={(event) =>
                                        field.onChange(event.currentTarget.checked)
                                    }
                                />
                                <Form.Check.Label className="mb-0">{t('hasBottle')}</Form.Check.Label>
                            </Form.Check>
                        )}
                    />
                </Form.Group>
            )}

            {isOil && (
                <Form.Group className="d-flex gap-4 mb-2 w-100">
                    <Form.Check
                        type="radio"
                        name="oilInputMode"
                        label={t('count')}
                        checked={oilInputMode === 'count'}
                        onChange={() => {
                            setOilInputMode('count');
                            resetField('availableAmount');
                        }}
                    />
                    <Form.Check
                        type="radio"
                        name="oilInputMode"
                        label={t('availableAmount')}
                        checked={oilInputMode === 'millilitres'}
                        onChange={() => {
                            setOilInputMode('millilitres');
                            setValue('count', 1);
                        }}
                    />
                </Form.Group>
            )}

            {(!isOil || oilInputMode === 'count') && <Form.Group
                className=" d-flex flex-column mb-2 align-items-start w-100 "
                controlId="count"
            >
                <CommonInputText
                    id="count"
                    label={t('count')}
                    defaultValue={String(itemToEdit?.count ?? '')}
                    labelTextColor="text-black"
                    type="number"
                    fieldError={formState.errors.count}
                    hookRegister={register('count', {
                        required: {
                            value: true,
                            message: t('count'),
                        },
                    })}
                />
            </Form.Group>}

            {isOil && oilInputMode === 'millilitres' && (
                <Form.Group
                    className=" d-flex flex-column mb-2 align-items-start w-100 "
                    controlId="availableAmount"
                >
                    <CommonInputText
                        id="availableAmount"
                        label={t('availableAmount')}
                        defaultValue={String(itemToEdit?.availableAmount ?? '')}
                        labelTextColor="text-black"
                        type="number"
                        step={5}
                        fieldError={formState.errors.availableAmount}
                        hookRegister={register('availableAmount', {
                            required: {
                                value: true,
                                message: t('availableAmount'),
                            },
                            min: {
                                value: 1,
                                message: t('availableAmount'),
                            },
                            validate: (value) => {
                                const amount = Number(value);
                                return (Number.isInteger(amount) && amount % 5 === 0)
                                    || String(t('availableAmount'));
                            },
                            setValueAs: (v) => (v === '' || v == null ? undefined : Number(v)),
                        })}
                    />
                </Form.Group>
            )}

            <Form.Group
                className=" d-flex flex-column mb-2 align-items-start w-100 "
                controlId="batchCode"
            >
                <CommonInputText
                    id="batchCode"
                    label={t('batchCode')}
                    defaultValue={defaultBatchCode}
                    labelTextColor="text-black"
                    type="text"
                    fieldError={formState.errors.batchCode}
                    hookRegister={register('batchCode', {
                        required: {
                            value: true,
                            message: t('batchCode'),
                        },
                    })}
                />
            </Form.Group>
            <Form.Group
                className=" d-flex flex-column mb-2 align-items-start w-100 "
                controlId="sapCode"
            >
                <CommonInputText
                    id="sapCode"
                    label={t('sapCode')}
                    defaultValue={String(itemToEdit?.sapCode ?? '')}
                    labelTextColor="text-black"
                    type="text"
                    fieldError={formState.errors.sapCode}
                    hookRegister={register('sapCode', {
                        required: {
                            value: false,
                            message: t('sapCode'),
                        },
                    })}
                />
            </Form.Group>
            <Form.Group
                className=" d-flex flex-column mb-2 align-items-start w-100 "
                controlId="equipment"
            >
                <CommonInputText
                    id="equipment"
                    label={t('equipment')}
                    defaultValue={String(itemToEdit?.equipment ?? '')}
                    labelTextColor="text-black"
                    type="text"
                    fieldError={formState.errors.equipment}
                    hookRegister={register('equipment', {
                        required: {
                            value: false,
                            message: t('equipment'),
                        },
                    })}
                />
            </Form.Group>
            <Form.Group
                className=" d-flex flex-column mb-2 align-items-start w-100 "
                controlId="cost"
            >
                <CommonInputText
                    id="cost"
                    label={t('cost')}
                    defaultValue={String(itemToEdit?.cost ?? '')}
                    labelTextColor="text-black"
                    type="number"
                    fieldError={formState.errors.cost}
                    hookRegister={register('cost', {
                        min: 1,
                        required: {
                            value: false ,
                            message: t('cost'),
                        },
                    })}
                />
            </Form.Group>
            <Form.Group
                className=" d-flex flex-column  align-items-start w-100 "
                controlId="description"
            >
                <CommonInputText
                    id="description"
                    label={t('description')}
                    defaultValue={String(itemToEdit?.description ?? '')}
                    labelTextColor="text-black"
                    type="text"
                    fieldError={formState.errors.description}
                    hookRegister={register('description', {
                        required: {
                            value: false,
                            message: t('description'),
                        },
                    })}
                />
            </Form.Group>
        </Form>
    );
};
